// This is a modified and typed version of a toast component
import React, { type FC, useEffect, useMemo } from 'react'

import * as ToastPrimitive from '@radix-ui/react-toast'

import IconClose from '@/assets/icons/icon-close.svg'
import { setToastFunction } from '@/models/toasts.ts'
import { classNames } from '@/utils/helpers.ts'

const ToastContext = React.createContext<
  ReturnType<typeof useRichDispatch> | undefined
>(undefined)
const InternalToastContext = React.createContext<
  | {
      toastElementsMapRef: React.MutableRefObject<Map<string, HTMLElement>>
      sortToasts: () => void
    }
  | undefined
>(undefined)

export interface Toast {
  status: 'default' | 'success' | 'error' | 'warning'

  type?: 'background' | 'foreground'
  duration?: number
  title: string
  description?: string
  open: boolean
  closingDisabled?: boolean
  autoCloseDisabled?: boolean
  actionText?: string
  action?: () => void
  onClose?: () => void
  as?: FC<ToastProps>
}

function useRichDispatch(dispatch: (toast: Toast) => void) {
  return useMemo(
    () =>
      Object.assign(dispatch, {
        success: (
          title: string,
          payload?: Omit<Toast, 'open' | 'status' | 'title'>,
        ) =>
          dispatch({
            ...payload,
            title,
            status: 'success',
            open: true,
          }),
        error: (
          title: string,
          payload?: Omit<Toast, 'open' | 'status' | 'title'>,
        ) =>
          dispatch({
            ...payload,
            title,
            status: 'error',
            open: true,
          }),
        warning: (
          title: string,
          payload?: Omit<Toast, 'open' | 'status' | 'title'>,
        ) =>
          dispatch({
            ...payload,
            title,
            status: 'warning',
            open: true,
          }),
        info: (
          title: string,
          payload?: Omit<Toast, 'open' | 'status' | 'title'>,
        ) =>
          dispatch({
            ...payload,
            title,
            status: 'default',
            open: true,
          }),
      }),
    [dispatch],
  )
}

type ToastViewportProps = React.PropsWithChildren<{
  outAnimationDelay?: number
  closeAfter?: number
}>

const defaultOutAnimationDelay = 300
const defaultCloseAfter = 50_000

export const ToastViewport = ({
  children,
  outAnimationDelay,
  closeAfter,
  ...props
}: ToastViewportProps) => {
  const [toasts, setToasts] = React.useState(new Map<string, Toast>())
  const toastElementsMapRef = React.useRef(new Map<string, HTMLElement>())
  const viewportRef = React.useRef<HTMLOListElement | null>(null)

  const sortToasts = React.useCallback(() => {
    const toastElements = Array.from(toastElementsMapRef.current).reverse()
    const heights: number[] = []

    toastElements.forEach(([, toast], index) => {
      if (!toast) {
        return
      }
      const height = toast.clientHeight

      heights.push(height)
      const frontToastHeight = heights[0]

      toast.setAttribute('data-front', String(index === 0))
      toast.setAttribute('data-hidden', String(index > 2))
      toast.style.setProperty('--index', String(index))
      toast.style.setProperty('--height', `${height}px`)
      toast.style.setProperty('--front-height', `${frontToastHeight}px`)
      const hoverOffsetY = heights
        .slice(0, index)
        .reduce((res, next) => res + next, 0)

      toast.style.setProperty('--hover-offset-y', `-${hoverOffsetY}px`)
    })
  }, [])

  const handleAddToast = React.useCallback(
    (toast: Toast) => {
      const key = String(Date.now())

      setToasts((currentToasts) => {
        const newMap = new Map(currentToasts)

        newMap.set(key, { ...toast, open: true })

        return newMap
      })

      if (!toast.closingDisabled && !toast.autoCloseDisabled) {
        setTimeout(() => {
          setToasts((currentToasts) => {
            const newMap = new Map(currentToasts)

            newMap.delete(key)

            return newMap
          })
        }, closeAfter ?? defaultCloseAfter)
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  const handleRemoveToast = React.useCallback((key: string) => {
    setToasts((currentToasts) => {
      const newMap = new Map(currentToasts)

      newMap.delete(key)

      return newMap
    })
  }, [])

  const handleDispatch = React.useCallback(
    (payload: Toast) => handleAddToast({ ...payload }),
    [handleAddToast],
  )

  React.useEffect(() => {
    const viewport = viewportRef.current

    if (viewport) {
      const handleFocus = () => {
        toastElementsMapRef.current.forEach((toast) => {
          toast.setAttribute('data-hovering', 'true')
        })
      }

      const handleBlur = (event: FocusEvent) => {
        if (
          !viewport.contains(event.target as Node) ||
          viewport === event.target
        ) {
          toastElementsMapRef.current.forEach((toast) => {
            toast.setAttribute('data-hovering', 'false')
          })
        }
      }

      viewport.addEventListener('pointermove', handleFocus)
      viewport.addEventListener('pointerleave', handleBlur)
      viewport.addEventListener('focusin', handleFocus)
      viewport.addEventListener('focusout', handleBlur)

      return () => {
        viewport.removeEventListener('pointermove', handleFocus)
        viewport.removeEventListener('pointerleave', handleBlur)
        viewport.removeEventListener('focusin', handleFocus)
        viewport.removeEventListener('focusout', handleBlur)
      }
    }
  }, [])

  const richDispatch = useRichDispatch(handleDispatch)

  useEffect(() => {
    // hack to use toasts outside of view layer
    setToastFunction(richDispatch)
  }, [richDispatch])

  return (
    <ToastContext.Provider value={richDispatch}>
      <InternalToastContext.Provider
        value={React.useMemo(
          () => ({
            toastElementsMapRef,
            sortToasts,
          }),
          [sortToasts],
        )}
      >
        <ToastPrimitive.Provider {...props}>
          {children}
          {Array.from(toasts).map(([key, { as: As, ...toast }]) => {
            const ToastComponent = As ?? Toast

            return (
              <ToastComponent
                key={key}
                id={key}
                toast={toast}
                onOpenChange={(open) => {
                  if (!open) {
                    toastElementsMapRef.current.delete(key)
                    sortToasts()
                    if (!open) {
                      setTimeout(() => {
                        handleRemoveToast(key)
                      }, outAnimationDelay ?? defaultOutAnimationDelay)
                    }
                  }
                }}
              />
            )
          })}
          <ToastPrimitive.Viewport
            ref={viewportRef}
            className="ToastViewport"
          />
        </ToastPrimitive.Provider>
      </InternalToastContext.Provider>
    </ToastContext.Provider>
  )
}

export const useToast = () => {
  const context = React.useContext(ToastContext)

  if (!context) {
    throw new Error('useToast must be used within a ToastViewport')
  }

  return context
}

export const useInternalContext = () => {
  const context = React.useContext(InternalToastContext)

  if (!context) {
    throw new Error('useInternalContext must be used within a ToastViewport')
  }

  return context
}

export type ToastProps = {
  toast: Omit<Toast, 'as'>
  id: string
  onOpenChange: (open: boolean) => void
} & ToastPrimitive.ToastProps

const Toast = ({ onOpenChange, toast, id, ...props }: ToastProps) => {
  const ref = React.useRef<HTMLLIElement | null>(null)
  const { sortToasts, toastElementsMapRef } = useInternalContext()
  const toastElementsMap = toastElementsMapRef.current

  React.useLayoutEffect(() => {
    if (ref.current) {
      toastElementsMap.set(id, ref.current)
      sortToasts()
    }
  }, [id, sortToasts, toastElementsMap])

  const swipeEventListener = (event: ToastPrimitive.SwipeEvent) => {
    if (toast.closingDisabled || toast.autoCloseDisabled) {
      event.preventDefault()
    }
  }

  return (
    <ToastPrimitive.Root
      {...props}
      ref={ref}
      type={toast.type}
      duration={toast.duration}
      className="ToastRoot"
      onOpenChange={onOpenChange}
      onSwipeStart={swipeEventListener}
      onSwipeMove={swipeEventListener}
      onSwipeEnd={swipeEventListener}
      onSwipeCancel={swipeEventListener}
    >
      <div
        className={classNames(
          'bg-card flex items-center gap-2 rounded-lg px-4 py-2 shadow-lg',
          {
            'bg-success-default': toast.status === 'success',
            'bg-danger-default': toast.status === 'error',
            'bg-warning-default': toast.status === 'warning',
          },
        )}
        data-status={toast.type}
      >
        <div className="col-2 flex flex-1 flex-col">
          <ToastPrimitive.Title className="w-auto text-sm font-semibold text-white">
            {toast.title}
          </ToastPrimitive.Title>
          <div>
            <ToastPrimitive.Description className="line-clamp-2 block w-full text-xs font-medium text-white/80">
              {toast.description}
            </ToastPrimitive.Description>
          </div>
        </div>
        {toast.actionText && toast.action && (
          <ToastPrimitive.Action
            altText={toast.actionText}
            className="focus-visible:ring-ring bg-card hover:bg-primary-50/80 dark:hover:bg-secondary-600 ml-auto rounded-md px-2 py-2 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-1"
            onClick={toast.action}
          >
            {toast.actionText}
          </ToastPrimitive.Action>
        )}
        {!toast.closingDisabled && (
          <ToastPrimitive.Close
            aria-label="Close"
            className="ml-auto self-start py-2"
            onClick={toast?.onClose}
          >
            <IconClose className="hover:text-secondary-50 text-white" />
          </ToastPrimitive.Close>
        )}
      </div>
    </ToastPrimitive.Root>
  )
}
